/**
 * Home, as this phone last saw it.
 *
 * The service worker answers a visit to home with its cached copy, marked
 * stale, so the page is on screen before the network has said anything. Every
 * row that needs the server is held until the page has fetched itself back;
 * solo is not, because it is played on the phone.
 *
 * The retrying is here, apart from the DOM, so tests can reach it.
 */

/** The pause before each retry. The last one repeats. */
export const RETRY_MS = [2000, 5000, 15000, 30000];

/**
 * Fetches home back until it arrives.
 *
 * `load` resolves once the fresh home is on the page and rejects when it could
 * not be had. `onState` hears 'updating' before every attempt, 'offline' after
 * each one that failed, and 'fresh' once.
 */
export function createFreshen({ load, onState = () => {}, scope = globalThis, delays = RETRY_MS } = {}) {
    let failures = 0;
    let timer = null;
    let loading = false;
    let fresh = false;

    async function attempt() {
        if (loading || fresh) return;
        scope.clearTimeout(timer);
        loading = true;
        onState('updating');
        try {
            await load();
            fresh = true;
            onState('fresh');
        } catch {
            onState('offline');
            timer = scope.setTimeout(attempt, delays[Math.min(failures, delays.length - 1)]);
            failures += 1;
        } finally {
            loading = false;
        }
    }

    return {
        start: attempt,
        /** Tries now rather than at the next retry: the connection came back. */
        retry: attempt,
    };
}
